import { City } from "@cryptors/api-client";
import { Box } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import type { CrudFilter } from "@refinedev/core";
import { IResourceComponentsProps } from "@refinedev/core";
import { EditButton, ShowButton, List, useDataGrid } from "@refinedev/mui";
import React, { useMemo } from "react";

import { DeleteIconButton } from "@/components/forms/DeleteIconButton";
import { SearchField } from "@/components/forms/SearchField";

import { formatLaunchDate } from "./city-form";

const buildSearchFilters = (q: string): CrudFilter[] => {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const orValue: CrudFilter[] = [
    { field: "name", operator: "containss", value: trimmed },
  ];
  if (/^\d+$/.test(trimmed)) {
    orValue.push({ field: "id", operator: "eq", value: Number(trimmed) });
  }
  return [{ operator: "or", value: orValue }];
};

export const CitiesList: React.FC<IResourceComponentsProps> = () => {
  const { dataGridProps, setFilters } = useDataGrid<City>({
    resource: "cities",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "id", order: "asc" }] },
  });

  const columns = useMemo<GridColDef<City>[]>(
    () => [
      {
        field: "id",
        headerName: "Id",
        type: "number",
        minWidth: 50,
      },
      {
        field: "name",
        headerName: "Nom",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "qrfyIds",
        headerName: "Qr codes associés",
        sortable: false,
        valueGetter: (_value, row) => row.qrfyIds.join(", ") || "—",
        minWidth: 200,
      },
      {
        field: "launchDate",
        headerName: "Date de lancement",
        valueGetter: (_value, row) => formatLaunchDate(row.launchDate) || "—",
        minWidth: 170,
      },
      {
        field: "location.latitude",
        headerName: "Latitude",
        sortable: false,
        valueGetter: (_value, row) => {
          return row.location?.latitude;
        },
        minWidth: 200,
      },
      {
        field: "location.longitude",
        headerName: "Longitude",
        sortable: false,
        valueGetter: (_value, row) => {
          return row.location?.longitude;
        },
        minWidth: 200,
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteIconButton recordItemId={row.id} />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 160,
      },
    ],
    [],
  );

  return (
    <List>
      <Box sx={{ mb: 2 }}>
        <SearchField
          placeholder="Recherche (nom, id)"
          buildFilters={buildSearchFilters}
          onSearch={setFilters}
        />
      </Box>
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </List>
  );
};
